import React, { useEffect, useState } from "react";
import { superadminAPI } from "../../../api/superadminClient";
import { TEMPLATE_META } from "./shared";
import BroadcastRecipients from "./BroadcastRecipients";

// Which tenants a broadcast reaches — matches BROADCAST_AUDIENCES in
// superadmin.service.js.
const BROADCAST_AUDIENCES = [
  { value: "all", label: "All tenants" },
  { value: "dms", label: "PLM tenants only" },
  { value: "manufacturing", label: "Manufacturing tenants only" },
];

// Email templates + the admin broadcast composer. Loads its own data the first
// time the tab is opened rather than on dashboard mount.
export default function EmailPanel({ onError }) {
  const [emailTemplates, setEmailTemplates] = useState({});
  const [templateEdits, setTemplateEdits] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    title: "",
    content: "",
    audience: "all",
  });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  // Who is ticked in the recipients list: { userIds, uniqueEmails }.
  const [broadcastSelection, setBroadcastSelection] = useState({ userIds: [], uniqueEmails: 0 });

  const load = async () => {
    try {
      const resp = await superadminAPI.getEmailTemplates();
      const tplMap = {};
      const tplEdits = {};
      (resp.data || []).forEach((tpl) => {
        tplMap[tpl.name] = tpl;
        tplEdits[tpl.name] = { subject: tpl.subject, htmlBody: tpl.htmlBody };
      });
      setEmailTemplates(tplMap);
      setTemplateEdits(tplEdits);
    } catch (err) {
      onError(err.response?.data?.message || "Failed to load email templates");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateEdit = (name, key, value) => {
    setTemplateEdits((prev) => ({
      ...prev,
      [name]: { ...prev[name], [key]: value },
    }));
  };

  const handleSaveTemplate = async (name) => {
    const payload = templateEdits[name];
    if (!payload) return;
    setSavingTemplate(true);
    onError("");
    try {
      const resp = await superadminAPI.saveEmailTemplate(name, payload);
      setEmailTemplates((prev) => ({ ...prev, [name]: resp.data }));
    } catch (err) {
      onError(err.response?.data?.message || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastForm.title || !broadcastForm.content) return;
    setSendingBroadcast(true);
    setBroadcastResult(null);
    onError("");
    try {
      const resp = await superadminAPI.sendBroadcast({
        ...broadcastForm,
        userIds: broadcastSelection.userIds,
      });
      setBroadcastResult(resp.data);
      setBroadcastForm((p) => ({ ...p, title: "", content: "" }));
    } catch (err) {
      onError(err.response?.data?.message || "Failed to send broadcast");
    } finally {
      setSendingBroadcast(false);
    }
  };

  return (
    <>
    <section className="superadmin-panel">
      <div className="superadmin-panel-head">
        <h2>Email Templates</h2>
        <p>
          Customize the emails sent by the platform. Changes take effect
          immediately.
        </p>
      </div>

      <div className="superadmin-table-wrap">
        <table className="superadmin-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Description</th>
              <th>Variables</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(TEMPLATE_META).map(([name, meta]) => (
              <React.Fragment key={name}>
                <tr>
                  <td>
                    <strong>{meta.label}</strong>
                  </td>
                  <td>{meta.description}</td>
                  <td>
                    {meta.variables.map((v) => (
                      <code
                        key={v}
                        style={{
                          background: "#f1f5f9",
                          borderRadius: 3,
                          padding: "1px 5px",
                          marginRight: 4,
                          fontSize: 11,
                        }}
                      >
                        {v}
                      </code>
                    ))}
                  </td>
                  <td>
                    {emailTemplates[name]?.isCustomized ? (
                      <span style={{ color: "#16a34a", fontSize: 12 }}>
                        Customized
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: 12 }}>
                        Default
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      className="superadmin-link-btn"
                      style={{ background: "#e0f2fe", color: "#075985" }}
                      onClick={() =>
                        setSelectedTemplate(
                          selectedTemplate === name ? null : name,
                        )
                      }
                    >
                      {selectedTemplate === name ? "Close" : "Edit"}
                    </button>
                  </td>
                </tr>

                {selectedTemplate === name && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        background: "#f8fbff",
                        padding: "20px 16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 24,
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{ display: "block", marginBottom: 12 }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0f2d5c",
                                display: "block",
                                marginBottom: 4,
                              }}
                            >
                              Subject
                            </span>
                            <input
                              type="text"
                              value={
                                templateEdits[name]?.subject ??
                                (emailTemplates[name]?.subject || "")
                              }
                              onChange={(e) =>
                                handleTemplateEdit(
                                  name,
                                  "subject",
                                  e.target.value,
                                )
                              }
                              style={{
                                width: "100%",
                                padding: "6px 10px",
                                border: "1px solid #cbd5e1",
                                borderRadius: 4,
                                fontSize: 13,
                                boxSizing: "border-box",
                              }}
                            />
                          </label>
                          <label
                            style={{ display: "block", marginBottom: 12 }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0f2d5c",
                                display: "block",
                                marginBottom: 4,
                              }}
                            >
                              HTML Body
                            </span>
                            <textarea
                              value={
                                templateEdits[name]?.htmlBody ??
                                (emailTemplates[name]?.htmlBody || "")
                              }
                              onChange={(e) =>
                                handleTemplateEdit(
                                  name,
                                  "htmlBody",
                                  e.target.value,
                                )
                              }
                              rows={14}
                              style={{
                                width: "100%",
                                padding: "6px 10px",
                                border: "1px solid #cbd5e1",
                                borderRadius: 4,
                                fontSize: 12,
                                fontFamily: "monospace",
                                resize: "vertical",
                                boxSizing: "border-box",
                              }}
                            />
                          </label>
                          <button
                            onClick={() => handleSaveTemplate(name)}
                            disabled={savingTemplate}
                          >
                            {savingTemplate
                              ? "Saving..."
                              : "Save Template"}
                          </button>
                        </div>
                        <div style={{ flex: 1 }}>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#0f2d5c",
                              display: "block",
                              marginBottom: 4,
                            }}
                          >
                            Preview
                          </span>
                          <div
                            style={{
                              border: "1px solid #e2e8f0",
                              borderRadius: 4,
                              minHeight: 300,
                              background: "#fff",
                              overflow: "auto",
                            }}
                            dangerouslySetInnerHTML={{
                              __html:
                                templateEdits[name]?.htmlBody ??
                                (emailTemplates[name]?.htmlBody ||
                                  "<p style='color:#94a3b8;padding:16px'>No template content yet.</p>"),
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: 24,
          padding: "20px",
          background: "#f0f9ff",
          borderRadius: 8,
          border: "1px solid #bae6fd",
        }}
      >
        <h3 style={{ margin: "0 0 4px", color: "#0f2d5c", fontSize: 15 }}>
          Compose & Send Broadcast
        </h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569" }}>
          Goes to the people ticked below: tenant admins and the alert
          recipients each admin has added (those with emails switched on,
          within the tenant&apos;s recipient limit). Uses the Broadcast
          template as a wrapper; nobody gets it twice.
        </p>
        {broadcastResult && (
          <div
            style={{
              background: "#dcfce7",
              border: "1px solid #86efac",
              borderRadius: 4,
              padding: "8px 12px",
              marginBottom: 12,
              fontSize: 13,
              color: "#166534",
            }}
          >
            Sent to {broadcastResult.sent} of {broadcastResult.total}{" "}
            recipients
            {broadcastResult.audience && broadcastResult.audience !== "all"
              ? ` (${BROADCAST_AUDIENCES.find((a) => a.value === broadcastResult.audience)?.label})`
              : ""}
            .
            {broadcastResult.failed > 0 &&
              ` (${broadcastResult.failed} failed — check server logs)`}
          </div>
        )}
        <fieldset style={{ border: "none", padding: 0, margin: "0 0 12px" }}>
          <legend
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#0f2d5c",
              marginBottom: 6,
              padding: 0,
            }}
          >
            Send to
          </legend>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {BROADCAST_AUDIENCES.map((a) => (
              <label
                key={a.value}
                style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="broadcast-audience"
                  value={a.value}
                  checked={broadcastForm.audience === a.value}
                  onChange={() => setBroadcastForm((p) => ({ ...p, audience: a.value }))}
                />
                {a.label}
              </label>
            ))}
          </div>
        </fieldset>
        <BroadcastRecipients
          audience={broadcastForm.audience}
          onSelectionChange={setBroadcastSelection}
          onError={onError}
        />
        <label style={{ display: "block", marginBottom: 10 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#0f2d5c",
              display: "block",
              marginBottom: 4,
            }}
          >
            Email Subject / Title
          </span>
          <input
            type="text"
            placeholder="e.g. Release Notes v2.4 - May 2026"
            value={broadcastForm.title}
            onChange={(e) =>
              setBroadcastForm((p) => ({ ...p, title: e.target.value }))
            }
            style={{
              width: "100%",
              maxWidth: 480,
              padding: "6px 10px",
              border: "1px solid #cbd5e1",
              borderRadius: 4,
              fontSize: 13,
              boxSizing: "border-box",
            }}
          />
        </label>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#0f2d5c",
              display: "block",
              marginBottom: 4,
            }}
          >
            Content (HTML or plain text)
          </span>
          <textarea
            placeholder="Write your message here..."
            value={broadcastForm.content}
            onChange={(e) =>
              setBroadcastForm((p) => ({ ...p, content: e.target.value }))
            }
            rows={6}
            style={{
              width: "100%",
              maxWidth: 600,
              padding: "6px 10px",
              border: "1px solid #cbd5e1",
              borderRadius: 4,
              fontSize: 13,
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
        </label>
        <button
          onClick={handleSendBroadcast}
          disabled={
            sendingBroadcast ||
            !broadcastForm.title ||
            !broadcastForm.content ||
            broadcastSelection.userIds.length === 0
          }
          style={{
            background: "#1e3a5f",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            padding: "8px 18px",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          {sendingBroadcast
            ? "Sending..."
            : `Send to ${broadcastSelection.uniqueEmails} recipient${broadcastSelection.uniqueEmails === 1 ? "" : "s"}`}
        </button>
      </div>
    </section>

    </>
  );
}
